import type { ReactNode } from "react"
import { createContext, useContext, useEffect, useState } from "react"

interface TimeContextType {
  currentTime: Date
}

const TimeContext = createContext<TimeContextType | undefined>(undefined)

interface TimeProviderProps {
  children: ReactNode
}

export function TimeProvider({ children }: TimeProviderProps) {
  const [currentTime, setCurrentTime] = useState(new Date())

  useEffect(() => {
    // Update the time every minute
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // 60 seconds

    // Also update immediately if the page becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setCurrentTime(new Date())
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  return (
    <TimeContext.Provider value={{ currentTime }}>
      {children}
    </TimeContext.Provider>
  )
}

export function useTime() {
  const context = useContext(TimeContext)
  if (context === undefined) {
    throw new Error("useTime must be used within a TimeProvider")
  }
  return context
}
